<template>
  <v-app>
    <!-- Redesigned pages are their own shell: header, nav and user drawer all
         live inside the page (via AppShell), so they render OUTSIDE the mobile /
         desktop layouts rather than inside their toolbar + bottom-nav frame. -->
    <template v-if="rendersOwnShell">
      <router-view />
    </template>
    <template v-else-if="isMobile">
      <mobile-layout />
    </template>
    <template v-else>
      <desktop-layout />
    </template>
    <notifications group="notify" position="bottom center" />
    <!-- <v-footer app></v-footer> -->

    <!-- Global AI Search Modal -->
    <ai-search-modal v-model="aiSearchModal" :open-mode="aiSearchOpenMode" :task-ref="aiSearchTaskRef" />

    <!-- Agent HTML response viewer -->
    <agent-result-modal
      :value="agentResultOpen"
      :html="agentResultHtml"
      :title="agentResultTitle"
      @input="onAgentResultInput"
    />

    <!-- Timezone backfill + travel detection -->
    <timezone-sync />
  </v-app>
</template>

<script>
import firebase from 'firebase/app';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { AgentResultModal } from '@routine-notes/ui/organisms';
import { installSwipeDismiss, uninstallSwipeDismiss } from '@routine-notes/ui/utils/swipeDismiss';
import {
  config, publicKey, isDevelopment, netlify,
} from './blob/config';
import {
  GC_NOTIFICATION_TOKEN,
} from './constants/settings';
import MobileLayout from './layouts/MobileLayout.vue';
import DesktopLayout from './layouts/DesktopLayout.vue';
import AiSearchModal from './containers/AiSearchModalContainer.vue';
import TimezoneSync from './components/TimezoneSync.vue';
import { isSignedIn, registerDevicePush } from './services/pushSession';
import eventBus, { EVENTS } from './utils/eventBus';

export default {
  components: {
    MobileLayout,
    DesktopLayout,
    AiSearchModal,
    AgentResultModal,
    TimezoneSync,
  },
  data() {
    return {
      drawer: null,
      mottoDialog: false,
      aiSearchModal: false,
      aiSearchOpenMode: 'add',
      aiSearchTaskRef: '',
      lastActiveTime: Date.now(),
    };
  },
  computed: {
    name() {
      return this.$root.$data.name;
    },
    email() {
      return this.$root.$data.email;
    },
    picture() {
      return this.$root.$data.picture;
    },
    pageTitle() {
      return (this.$route.name && this.$route.name[0].toUpperCase() + this.$route.name.substr(1))
        || 'Routine Notes';
    },
    isMobile() {
      return this.$vuetify.breakpoint.name === 'xs';
    },
    /**
     * A route that brings its own chrome — `AppShell` supplies the header, the
     * nav (phone bottom bar / tablet rail / desktop sidebar) and the drawer, so
     * wrapping it in MobileLayout/DesktopLayout would render two of each.
     *
     * `focusHome` is the original key, kept because `/home` and its deep link
     * still carry it; `appShell` is the same idea named for every other
     * redesigned page. Either one skips the legacy layouts.
     */
    rendersOwnShell() {
      const meta = this.$route.meta || {};
      return !!(meta.appShell || meta.focusHome);
    },
    activeAgentResultRoutineId() {
      return this.$agent.state.resultModalRoutineId;
    },
    agentResultOpen() {
      return !!this.activeAgentResultRoutineId
        && !!this.$agent.lastResultByRoutineId[this.activeAgentResultRoutineId];
    },
    agentResultHtml() {
      const taskRef = this.activeAgentResultRoutineId;
      const result = taskRef && this.$agent.lastResultByRoutineId[taskRef];
      return result && result.type === 'html' ? result.body : '';
    },
    agentResultTitle() {
      const taskRef = this.activeAgentResultRoutineId;
      const agent = taskRef && this.$agent.getByTaskRef(taskRef);
      return agent ? `${agent.name} — result` : 'Agent result';
    },
  },
  created() {
    // Listen for AI search open event
    eventBus.$on(EVENTS.OPEN_AI_SEARCH, (payload) => {
      this.aiSearchModal = true;
      this.aiSearchOpenMode = (payload && payload.mode) || 'add';
      // The routine the opener is showing (e.g. the focused Routine Focus
      // card); empty falls back to the clock-current routine.
      this.aiSearchTaskRef = (payload && payload.taskRef) || '';
    });

    // Refresh app if inactive for more than 15 minutes
    document.addEventListener('visibilitychange', this.handleAppVisibility);

    if (Capacitor.isNativePlatform()) {
      // Native logic using Capacitor Push Notifications
      this.initNativeFCM();
      // Check for soft keys on Android
      this.checkSoftKeys();
    } else {
      // PWA logic
      this.initPwaFCM();
    }
  },
  mounted() {
    // Swipe-down-to-dismiss for every v-dialog / v-bottom-sheet in the app
    // (and the chassis sheets that carry `v-swipe-dismiss`). One document
    // listener, so raw <v-dialog>s in pages get it without opting in.
    installSwipeDismiss();
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleAppVisibility);
    uninstallSwipeDismiss();
  },
  methods: {
    onAgentResultInput(open) {
      if (!open) this.$agent.closeResultModal();
    },
    handleAppVisibility() {
      if (document.visibilityState === 'hidden') {
        this.lastActiveTime = Date.now();
      } else if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - this.lastActiveTime;
        const fifteenMinutes = 15 * 60 * 1000;
        if (elapsed > fifteenMinutes) {
          console.log(`App inactive for ${Math.round(elapsed / 60000)} minutes, refreshing...`);
          window.location.reload();
        }
      }
    },

    async initPwaFCM() {
      if (isDevelopment || netlify) {
        firebase.initializeApp(config);

        const messaging = firebase.messaging();
        messaging.usePublicVapidKey(publicKey);

        try {
          // Request permission using Notification API
          const permission = await Notification.requestPermission();

          if (permission === 'granted') {
            console.log('Notification permission granted.');

            // Get Token
            const token = await messaging.getToken();
            console.log('FCM token:', token);
            localStorage.setItem(GC_NOTIFICATION_TOKEN, token);
            registerDevicePush(this.$apollo && this.$apollo.provider.defaultClient, token);
          } else {
            console.log('Notification permission denied.');
          }
        } catch (err) {
          console.log('Unable to get permission to notify.', err);
        }

        // Foreground messages
        messaging.onMessage((payload) => {
          console.log('Message received in foreground:', payload);
          if (!isSignedIn()) return;
          this.$notify({
            group: 'notify',
            title: payload.notification.title,
            text: payload.notification.body,
          });
        });
      }
    },

    async initNativeFCM() {
      // Request Local Notifications permission first
      const localPerm = await LocalNotifications.requestPermissions();
      if (localPerm.display !== 'granted') {
        console.warn('Local notification permission not granted');
        return;
      }
      if (Capacitor.getPlatform() === 'android') {
      // Create notification channel for Android
        await LocalNotifications.createChannel({
          id: 'routine-notifications',
          name: 'Routine Notifications',
          description: 'Notifications for routine updates and reminders',
          sound: 'default',
          importance: 5,
          visibility: 1,
          lights: true,
          lightColor: '#4285f4',
          vibration: true,
        });
        // Routines set to "Alarm" ring on their own channel, so the person can
        // give it a louder sound than ordinary reminders in system settings.
        await LocalNotifications.createChannel({
          id: 'routine-alarms',
          name: 'Routine Alarms',
          description: 'Routines you set to ring like an alarm',
          sound: 'default',
          importance: 5,
          visibility: 1,
          lights: true,
          lightColor: '#e8710a',
          vibration: true,
        });
      }

      PushNotifications.requestPermissions().then((result) => {
        console.log('pust notification result', result);
        if (result.receive === 'granted') {
          PushNotifications.register();
        }
      });

      PushNotifications.addListener('registration', (token) => {
        console.log('Device registered', token.value);
        localStorage.setItem(GC_NOTIFICATION_TOKEN, token.value);
        // Every start and every rotation: this device is one of possibly several.
        registerDevicePush(this.$apollo && this.$apollo.provider.defaultClient, token.value);
      });

      // Foreground display and taps (buttons included) are pushService's: it
      // re-posts with the routine's action buttons and routes every tap. A
      // second listener here showed each foreground push twice.
    },

    checkSoftKeys() {
      // Native WebView only — mobile browsers handle the status bar and
      // must never receive the android14-plus safe-area classes.
      if (!Capacitor.isNativePlatform()) return;
      // Android 14+ detection
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      const androidMatch = ua.match(/Android\s([0-9.]+)/);
      if (androidMatch) {
        const androidVersion = parseFloat(androidMatch[1]);
        if (androidVersion >= 14) {
          document.body.classList.add('android14-plus');
          document.documentElement.classList.add('android14-plus');
        }
      }
    },
  },
};
</script>

<style>
.v-toolbar--fixed {
  z-index: 5;
}

/* Toolbar positioning below safe area — native WebView only. On mobile
   web the browser owns the status bar and Vuetify sizes the toolbar
   itself (56px on xs); forcing 64px here made the web header too tall. */
.capacitor-native .v-toolbar {
  margin-top: max(var(--system-top-inset, 0px), env(safe-area-inset-top)) !important;
  padding-top: 0 !important;
  height: 64px !important;
}

/* Android 15 soft keys handling */
.android15-soft-keys {
  padding-bottom: 48px;
}

.android15-no-soft-keys {
  padding-bottom: 0;
}

@media (max-height: 600px) {
  .android15-soft-keys {
    padding-bottom: 40px;
  }
}
</style>
